import { useEffect, useRef, useState } from 'react';
import { useI18n } from '../i18n';

export interface ConfirmDialogProps {
  title: string;
  lines: Array<{ label: string; value: string }>;
  warnings?: string[];
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}

/** Modal confirmation used for cancel/reschedule drafts. Nothing is written until Confirm is pressed. */
export function ConfirmDialog({ title, lines, warnings = [], onConfirm, onCancel }: ConfirmDialogProps) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error'));
      setBusy(false);
    }
  };

  return (
    <div className="overlay" role="presentation">
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="confirm-dialog-title">
        <h3 id="confirm-dialog-title">{title}</h3>
        <dl className="conf-lines">
          {lines.map((l) => (
            <div key={l.label} style={{ display: 'contents' }}>
              <dt>{l.label}</dt>
              <dd>{l.value}</dd>
            </div>
          ))}
        </dl>
        {warnings.map((w) => <div key={w} className="cds">{w}</div>)}
        {error && <div className="err" role="alert">{error}</div>}
        <div className="conf-actions">
          <button ref={confirmRef} className="btn btn-primary" onClick={confirm} disabled={busy}>{busy ? t('confirming') : t('confirm')}</button>
          <button className="btn" onClick={onCancel} disabled={busy}>{t('reject')}</button>
        </div>
      </div>
    </div>
  );
}
